import { LOG_APPLY_MEMO, ROLE_ORDER, TZ } from "../const"
import i18n from "@/i18n"
import Banner from "@components/Common/Banner"
import { setContextData, setProductLevel, setProfileDataForCtx, setSelectedRole } from "@redux/user"
import { differenceInCalendarDays, differenceInCalendarYears, intervalToDuration } from "date-fns"
import { formatInTimeZone } from "@/overrides"
import { saveAs } from "file-saver"
import _, { isArray } from "lodash"
import { Alert } from "react-bootstrap"
import { Check, Dash, X } from "react-bootstrap-icons"
import { Menu, MenuItem } from "react-bootstrap-typeahead"
import { Trans } from "react-i18next"
import { serialize } from "object-to-formdata"
import { memo, type ComponentType, type KeyboardEvent } from "react"
import { useEventTypeDoc, useGetEventTypeDoc } from "./hooks"
import { isSuperUser } from "./customization/customization"
import { setConfirmModal } from "@redux/ui"
import type { Dispatch } from "redux"

const t = i18n.t

type AnyRecord = Record<string, any>

type EventDateOptions = {
	omitSingleDayDate?: boolean
	displayYear?: boolean
	omitEndTime?: boolean
	displayDuration?: boolean
}

type TestFieldLike = {
	testId?: any
	resultIndex?: number | string | null
	specialIndex?: number | string | null
	isGlobalSpecial?: boolean
	comparable?: boolean
	children?: TestFieldLike[]
	[key: string]: any
}

export const isNotEmpty = _.negate(_.isEmpty)

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms))

const getDurationString = (startDate: any, endDate: any) => {
	const duration = intervalToDuration({
		start: startDate,
		end: endDate,
	})
	return durationToString(duration)
}
const durationToString = (duration: any) => {
	const durationArray: string[] = []
	if (duration.years > 0) durationArray.push(duration.years + "y")
	if (duration.months > 0) durationArray.push(duration.months + "kk")
	if (duration.days > 0) durationArray.push(duration.days + "pv")
	if (duration.hours > 0) durationArray.push(duration.hours + "t")
	if (duration.minutes > 0) durationArray.push(duration.minutes + "m")
	return durationArray.join(" ")
}

// Get default time hours and minutes for survey or other repeating tasks
export const getDefaultTaskTimes = (eventStart: string | number | Date = new Date()): [Date, Date] => {
	const time = new Date(eventStart)
	time.setHours(0)
	time.setMinutes(0)
	time.setSeconds(0)
	const defaultStart = new Date(time)
	time.setHours(23)
	time.setMinutes(59)
	time.setSeconds(59)
	const defaultEnd = new Date(time)
	return [defaultStart, defaultEnd]
}

// Checks if current time is after task `closesAt` value
// Used for getting age from birthdate
export const getAge = (birthDate: string | number | Date) =>
	Math.floor((new Date().getTime() - new Date(birthDate).getTime()) / 3.15576e10)

// Used for downloading personal data as a JSON file
export const downloadProfile = (profileData: any, userData: any) => {
	if (!profileData || !userData) return
	let listedUserData: any = {
		Firstname: profileData?.firstname,
		Lastname: profileData?.lastname,
		Username: userData?.username,
		Email: userData?.email,
		Eenet_email: userData?.eenet_email,
		Eenetid: userData?.eenetid ?? profileData?.eenetid,
		Phone: profileData?.phone,
		Address: profileData?.address,
		AddressZip: profileData?.addressZip,
		AddressLocality: profileData?.addressLocality,
		Birthday: profileData?.birthday && formatInTimeZone(new Date(profileData?.birthday), TZ, "dd/MM/yyyy"),
		Foot: profileData?.foot,
		Age: profileData?.age && Math.floor(profileData?.age),
		PalloID: profileData?.palloid,
		SporttiID: profileData?.sporttiId,
		Gender: profileData?.sex,
		Injury: profileData?.injury,
	}
	if (profileData?.parents.length > 0) listedUserData.Parents = profileData?.parents.map((parent: any) => parent?.name)
	if (profileData?.children.length > 0) listedUserData.Children = profileData?.children.map((child: any) => child?.name)
	if (profileData?.watches.length > 0) listedUserData.Watches = profileData?.watches.map((watch: any) => watch?.name)
	listedUserData = Object.fromEntries(Object.entries(listedUserData).filter(([, v]) => v !== ""))
	listedUserData = JSON.stringify(listedUserData, null)
	const blob = new Blob([listedUserData], { type: "text/plain;charset=utf-8" })
	saveAs(blob, `${profileData?.firstname} ${profileData?.lastname}.json`)
}

// Only include roles that should be selectable
export const roleFilter = (roles: string[]) =>
	roles.filter((role) => ["basic_user", "admin", "patron", "club_support_admin"].includes(role))

export const productLevelFilter = (roles: string[]) =>
	roles.filter((role) => ["prod1", "prod2", "prod3", "prod4", "prod5"].includes(role))

/**
 * Convenience function for sorting values by their order of appearance in a reference array.
 *
 * Matching is case insensitive. Not found values are pushed to the end of the sorted list.
 *
 * @param {[String | Object]} targetArr       The array to be sorted. Can contain strings or objects; the latter will need path argument specified.
 * @param {[String]} referenceArr             The reference array that sorting is based on.
 * @param {String} path                       If the array to be sorted contains object, path is used for getting the associated string value
 * @returns {[String | Object]}               Sorted `targetArr` array
 */
export const sortByArray = (targetArr: any[], referenceArr: string[], path?: string) => {
	return targetArr.sort((a, b) => {
		let valA = path ? _.get(a, path) : a
		let valB = path ? _.get(b, path) : b
		if (_.isObject(valA)) valA = (valA as AnyRecord).name
		if (_.isObject(valB)) valB = (valB as AnyRecord).name
		if (!_.isString(valA) || !_.isString(valB)) return 0
		const indexA = referenceArr.findIndex((str) => str.toLowerCase() === valA.toLowerCase())
		const indexB = referenceArr.findIndex((str) => str.toLowerCase() === valB.toLowerCase())
		if (indexA === -1 && indexB === -1) return 0
		if (indexA === -1) return 1
		if (indexB === -1) return -1
		return indexA - indexB
	})
}

// Sorts group roles in preset order
export const sortGroupRoles = (roles: any) => {
	if (!roles || !Array.isArray(roles)) return []
	const copy = [...roles]
	const reference = ["headcoach", "coach", "player", "manager", "Valmennuspäällikkö", "Valmentaja", "Pelaaja", "Joukkueenjohtaja"]
	if (copy.every((o) => _.isObject(o))) {
		return sortByArray(copy, reference, "name")
	} else {
		return sortByArray(copy, reference)
	}
}

// Sorts roles in descending order
export const sortRoles = (roles: any) => {
	if (!roles || !Array.isArray(roles)) return []
	const copy = [...roles]
	const reference = ["admin", "basic_user", "prod5", "prod4", "prod3", "prod2", "prod1"]
	return sortByArray(copy, reference)
}

export const splitFilter =
	(str: string, getField: (obj: any) => string = (obj: any) => obj.name, op: "every" | "some" = "every") =>
	(obj: any) => {
		if (!str) return true
		const value = getField(obj).toLowerCase()
		return str
			.toLowerCase()
			.split(" ")
			[op]((term) => value.includes(term))
	}
/*
	Temporary solution based on current event types. 
	TODO: Replace with a more robust solution.
*/
export const dragAndDropIsSupported = () => {
	const div = document.createElement("div")
	const enabled = "draggable" in div || ("ondragstart" in div && "ondrop" in div)
	div.remove()
	return enabled
}

/**
 * Converts what would conventionally be JSON body
 * data to equivalent FormData object.
 *
 * Backend body-parser 'extended' option should be set to true
 * to allow array and object parsing of form data.
 *
 * @param {Object} values       Object that contains form data as field-value pairs
 * @returns {FormData}          FormData result
 */
export const convertObjToFormData = (values: any) => {
	const formData = serialize(values, { indices: true, allowEmptyArrays: true })

	// Iterate form data entries and move file entries to the end; required by backend streaming file upload implementation
	const newFormData = new FormData()
	const files: any[] = []
	for (const [fieldname, value] of formData.entries()) {
		if (value instanceof File) {
			const file = formData.get(fieldname)
			files.push([fieldname, file])
		} else {
			newFormData.append(fieldname, value)
		}
	}
	for (const [fieldname, file] of files) {
		newFormData.append(fieldname, file)
	}

	return newFormData
}

export const convertObjToQueryStr = (obj: any) => {
	if (!obj || _.isEmpty(obj)) return ""
	let str = "?"
	Object.keys(obj).forEach((key) => {
		if (Array.isArray(obj[key])) {
			obj[key].forEach((val) => {
				val = encodeURIComponent(val)
				str = str.concat(`${key}=${val}&`)
			})
		} else {
			let val = obj[key]
			if (val != null && val !== "") {
				val = encodeURIComponent(val)
				str = str.concat(`${key}=${val}&`)
			}
		}
	})
	// Remove trailing & if present
	if (str[str.length - 1] === "&") str = str.slice(0, str.length - 1)
	return str
}

// Number array functions (mainly for aggregating multiple samples)
/**
 * Returns an iterable of all identity combinations for round, result
 * definition and sample indexes. Use spread operator to convert it to
 * array form.
 *
 * @param {Object} test           Training document; used for checking number of result definitions and their samples
 * @param {Number} rounds         Number of rounds; usually derived from protocol attached to test instance
 * @returns {Iterable}            Iterable of all identity combinations.
 */
export const getResultIndexIterable = (test: any, rounds = 1) => {
	const combinations: number[][] = []
	for (let i = 0; i < rounds; i++) {
		for (const [j, resultDef] of test.result.entries()) {
			const samples = resultDef.samples
			for (let k = 0; k < samples; k++) {
				combinations.push([i, j, k])
			}
		}
	}

	// Create iterable
	const iter: any = {}
	iter[Symbol.iterator] = function () {
		let done = false
		let n = 0
		const arrs = combinations

		return {
			next() {
				const arr = arrs[n]
				if (!arr) {
					done = true
				}
				n += 1
				return { value: arr, done }
			},
		}
	}

	return iter
}

// Returns the length of result index iterable
// Returns the index place of a particular identity in iterable
/**
 * Returns all nested keys in an object via recursion. Recursion stops when a non-object
 * leaf value is encountered.
 *
 * @param {Object} obj      Object whose nested keys are retrieved
 * @param {Array} arr       Starting array used in recursion
 * @returns {[String]}      Array of nested object keys
 */
export const getAllNestedKeys = (obj: any, arr: string[] = []) => {
	if (!_.isObject(obj)) return arr
	const entries = Object.entries(obj)
	for (const entry of entries) {
		const key = entry[0]
		const val = entry[1]
		arr.push(key)
		getAllNestedKeys(val, arr)
	}
	return arr
}

/**
 * Returns all nested values in an object via recursion. Recursion stops when non-object
 * primitive is encountered.
 *
 * @param {Object} obj      Object whose nested keys are retrieved
 * @param {Array} arr       Starting array used in recursion
 * @returns {[String]}      Array of nested object keys
 */
export const getAllNestedValues = (obj: any, arr: any[] = []) => {
	if (!_.isObject(obj)) {
		arr.push(obj)
		return _.uniq(arr)
	}
	const entries = Object.entries(obj)
	for (const entry of entries) {
		const val = entry[1]
		getAllNestedValues(val, arr)
	}
	return _.uniq(arr)
}

export const checkIdRequired = (profileData: any, sportNames: string[]) => {
	if (!profileData?.groupRoleOverview || !sportNames) return false

	const hasSport = profileData?.opClassList?.some((str: string) => sportNames.includes(str))
	const idRequired = profileData?.combinedGeneralPerms?.includes("palloIdRequired")
	return hasSport && idRequired
}

/**
 * Utility function for getting a default initial invite row or an additional row that copies some of the first invitations's values.
 *
 * Used in InviteTableInput and components embedding it.
 */
export const getDefaultInvite = (invites: any[], initialGroup = "", initialGroupRole = "") => {
	const getNewRowId = () => {
		let rowId = new Date().getTime()
		const rowIds = invites.map((invite) => invite.rowId)
		while (rowIds.includes(rowId)) rowId++
		return rowId
	}

	return {
		email: "",
		firstname: "",
		lastname: "",
		group: invites.length ? invites[0]?.group : initialGroup,
		groupRole: invites.length ? invites[0]?.groupRole : initialGroupRole,
		rowId: getNewRowId(),
		child: invites.length ? invites[0]?.child : initialGroupRole === "player" ? true : false,
		language: "fi",
	}
}
export const generateUsername = (firstname: string, lastname: string, confirmation?: { email?: string }) => {
	const num = Math.random().toString().slice(2, 6).padEnd(4, "0")

	if (firstname && lastname) {
		const namePart = `${firstname}${lastname}`.slice(0, 20)
		return `${namePart}${num}`
	}
	if (confirmation) {
		const emailPart = confirmation.email?.split("@")?.[0]?.slice(0, 7) ?? "käyttäjä"
		return `${emailPart}${num}`
	}
	return `käyttäjä${num}`
}

export const getBanners = (profileData: any, navigate: any) => {
	if (!profileData) return null
	return profileData?.pendingChildAccounts?.map((child: any, i: number) => {
		const { firstname, lastname, accountType } = child
		if (accountType === "register") {
			return (
				<Banner key={i}>
					<div className="text-center">
						<Trans t={t}>
							Sinun tulee{" "}
							<Alert.Link
								onClick={() => {
									navigate("/registerchild", {
										state: { ...child, pendingChildAccounts: profileData?.pendingChildAccounts },
									})
								}}
							>
								rekisteröidä
							</Alert.Link>{" "}
							huollettava{" "}
							<strong>
								{{ firstname } as any} {{ lastname } as any}
							</strong>{" "}
							käyttäjäksi!
						</Trans>
					</div>
				</Banner>
			)
		} else if (accountType === "activate") {
			return (
				<Banner key={i}>
					<Trans t={t}>
						Sinun pitää{" "}
						<Alert.Link
							onClick={() => {
								navigate("/activatechild", {
									state: { ...child, pendingChildAccounts: profileData?.pendingChildAccounts },
								})
							}}
						>
							aktivoida
						</Alert.Link>{" "}
						huollettava{" "}
						<strong>
							{{ firstname } as any} {{ lastname } as any}
						</strong>{" "}
						käyttäjäksi!
					</Trans>
				</Banner>
			)
		} else {
			return (
				<Banner key={i}>
					<Trans t={t}>
						Sinun täytyy{" "}
						<Alert.Link
							onClick={() => {
								navigate("/child_invite_confirm", {
									state: { ...child, pendingChildAccounts: profileData?.pendingChildAccounts },
								})
							}}
						>
							hyväksyä
						</Alert.Link>{" "}
						huollettava{" "}
						<strong>
							{{ firstname } as any} {{ lastname } as any}
						</strong>{" "}
						uuteen ryhmään!
					</Trans>
				</Banner>
			)
		}
	})
}
/**
 * Gets the referenced tests in protocol or single test.
 *
 * @param {Object} item             Training object. Should have defined `result` path.
 * @param {[Object]} testData       List of test data and their definitions.
 * @returns                         Empty string or comma-separated list of test names.
 */
export const getReferencedTests = (item: any, testData?: any[]) => {
	if (!testData) return ""
	let referencedTests: any[] = []
	let arr: any[] = []
	// Item is a protocol
	if (item.testOrder) {
		arr = item.tests
		// Item is a test
	} else {
		arr = [item]
	}
	for (const test of arr) {
		const fullTest = testData?.find((t) => t?._id === test?._id)
		if (!fullTest) continue
		let refTests = fullTest.result.reduce((a: any[], b: any) => a.concat(b.referencedTest), [])
		refTests = _.compact(refTests)
		referencedTests = referencedTests.concat(refTests)
	}
	const fullReferenceTests = referencedTests.map((id) => testData?.find((test) => id === test._id))
	return fullReferenceTests.map((test) => test?.name).join(", ")
}

/**
 * Returns boolean on whether an aggregate should be displayed for a training result.
 *
 * Aggregates like average, maximum, minimum etc. often do not make
 * sense when they have multiple result definitions with different units.
 *
 * @param {Object} resultDefinitions            Object that contains result definitions
 * @returns {Boolean}                           Whether an aggregate is applicable
 */
export const noAggDisplay = (resultDefinitions: any[] = []) => {
	return _.uniqBy(resultDefinitions, "unit").length > 1
}

/**
 * Performs a check on the input value dependent on current root group context
 * and whether user is currently admin.
 *
 * @param {String} rootGroupId      Root Group Id that context would be checked against
 * @returns {Boolean}               Whether value is matched with context
 */
export const rootCtxBool = (userState: any, rootGroupId: any) => {
	const contextData = userState?.contextData
	const isAdmin = isSuperUser(userState?.selectedRole)
	return isAdmin || contextData?.rootGroup === rootGroupId
}
/**
 * Sets context state values based on input profile data. Context is used in various permission checks and conditional rendering.
 *
 * Validity of profile context values is handled in the backend during own profile's GET query.
 *
 * @param {Object} data 					Profile data for which context is set to.
 * @param {Function} dispatch 		Dispatch function from component useDispatch() hook
 * @returns												Context object of rootGroup, group and groupRole
 */
export const initializeCtx = (data: any, dispatch: any) => {
	const obj = data?.settings?.context
	if (!obj) return { rootGroup: "", group: "", groupRole: "", adminToggle: false }
	const { rootGroup, group, groupRole, adminToggle } = obj
	dispatch(setContextData({ rootGroup, group, groupRole, adminToggle }))
	dispatch(setProfileDataForCtx(data))

	const adminAvailable = !!data?.dnInfo?.isAdmin
	if (adminToggle && adminAvailable) {
		const roles: { name: string }[] = data.user.roles
		if (roles.some((r) => r.name === "admin")) {
			dispatch(setSelectedRole("admin"))
		} else if (roles.some((r) => r.name === "expert")) {
			dispatch(setSelectedRole("expert"))
		}
	} else {
		dispatch(setSelectedRole("basic_user"))
	}

	const curRoot = data.rootGroups.find((rg: any) => rg._id === rootGroup)
	if (curRoot) {
		dispatch(setProductLevel(curRoot.productLevel))
	}
	if (adminToggle) {
		dispatch(setProductLevel(5))
	}

	const returnVal = { rootGroup, group, groupRole, adminToggle }
	return returnVal
}

/**
 * Handle various path configurations for participants / profile lists and return profile name and role text
 *
 * @param {Object} option 										Object that contains some path to profile-attached groups
 * @param {String[]=} groupRoleGroupIds 				Array of group ids that all groups of profile are filtered agains
 * @returns {[String, String, String | null]}								Profile name and comma-separated role titles
 */
export const getProfileNameAndRole = (
	profile: any,
	rootGroupId: any,
	groupRoleGroupIds: any[] = [],
	shortenGroups: boolean,
	getRoleTitle?: any
) => {
	let roleText = t("Ei ryhmärooleja")
	const profileRef = profile.profile ?? profile
	const profileName = profileRef?.name ?? `${profileRef.lastname} ${profileRef.firstname}`
	const overview = profileRef?.groupRoleOverview

	if (!getRoleTitle) {
		getRoleTitle = (str: string) => str
	}

	if (groupRoleGroupIds) {
		groupRoleGroupIds = groupRoleGroupIds.map((g) => g._id ?? g)
	}

	if (!overview) {
		const memberRoles = _.get(profileRef, "dnInfo.memberRoles")
		if (!memberRoles) {
			return [profileName, roleText, null]
		}
		const rolesInGroups = memberRoles.filter((mr: any) =>
			groupRoleGroupIds?.length > 0 ? groupRoleGroupIds.some((gId: any) => mr.groupIds.includes(gId)) : true
		)
		if (_.isEmpty(rolesInGroups)) {
			return [profileName, roleText, null]
		}
		const roleTitles = rolesInGroups.map((mr: any) => getRoleTitle(mr.title))
		roleText = roleTitles.join(", ")
		return [profileName, roleText, null]
	}

	const filteredOverview = rootGroupId ? _.pick(overview, rootGroupId) : overview
	const allRoots = _.values(filteredOverview).filter((o) => _.isObject(o)) as any[]
	const allGroups = allRoots.reduce((a: any[], b: any) => {
		const groups = b.groups.map((gId: any) => ({ ...b[gId], id: gId }))
		return a.concat(...groups)
	}, [])
	const filteredGroups = allGroups.filter((g) => (groupRoleGroupIds?.length > 0 ? groupRoleGroupIds?.includes(g.id) : true))
	const filteredTitles = _.uniq(_.flatMap(filteredGroups, "memberRoles").map((r) => getRoleTitle(r.title)))
	roleText = filteredTitles.join(", ")
	let groups = filteredGroups.map((g) => g.name).join(", ")

	if (filteredGroups.length > 2 && shortenGroups) {
		groups = t("Useita ryhmiä")
	}
	return [profileName, roleText, groups]
}

// Use in 'onKeyDown' handler in inputs to prevent typing of non-positive non-integers
export const blockNotPosIntegers = (e: KeyboardEvent<HTMLInputElement>) =>
	["e", "E", "+", "-", ",", "."].includes(e.key) && e.preventDefault()

// Use in 'onKeyDown' handler to go down one input when pressing enter key in test result input page
export const handleEnter = (
	event: any,
	_resultDef: any,
	trainee: any,
	sampleDx: number,
	trainees: any[],
	tests: any,
	roundDx: number,
	testId: any,
	resultDefDx: number
) => {
	if (event.keyCode === 13) {
		// Enter key has been pressed on a text input so we navigate down
		const form = event.target.form
		const inputs = form.querySelectorAll("input[type='number']") // Get all inputs with the type number in form (this includes ALL inputs, event the ones that are in different tabs)
		const currentFocusIndex = Array.from(inputs).indexOf(event.target) // Currently focused input in the inputs
		const traineeIndex = trainees.findIndex((person) => person?.profile?._id === trainee?.profile?._id)
		const currentTest = isArray(tests) ? tests.find((te) => te._id === testId) : tests
		let textInputsPerRow = 0
		let rounds = 0
		if (isArray(tests)) {
			for (let i = 0; i < tests.length; i++) {
				rounds = tests[i]?.rounds ? tests[i]?.rounds : 1
				textInputsPerRow += _.sumBy(tests[i]?.result, "samples") * rounds
			}
		} else {
			rounds = tests?.rounds ? tests?.rounds : 1
			textInputsPerRow = _.sumBy(tests?.result, (result) => _.get(result, "samples", 0)) * rounds
		}
		const textInputsInOneSlot = currentTest?.result[resultDefDx]?.samples
			? currentTest?.result[resultDefDx]?.samples * rounds
			: currentTest?.rounds
		if (
			textInputsInOneSlot > (sampleDx + 1) * (roundDx + 1) ||
			(textInputsInOneSlot === (sampleDx + 1) * (roundDx + 1) &&
				currentTest?.result?.length === 1 &&
				traineeIndex < trainees.length - 1 &&
				!isArray(tests))
		) {
			inputs[currentFocusIndex + 1].focus()
		} else if (traineeIndex < trainees.length - 1 && textInputsInOneSlot === (sampleDx + 1) * (roundDx + 1)) {
			inputs[currentFocusIndex + textInputsPerRow - textInputsInOneSlot + 1].focus()
		} else if (traineeIndex === trainees.length - 1) {
			inputs[currentFocusIndex - textInputsPerRow * (trainees.length - 1) - textInputsInOneSlot + 1].focus()
		}

		event.preventDefault()
	}
}
export const getGroupedRenderMenu =
	({ groupByPath, hasNextPage = false, labelKey, disableSort = false }: any) =>
	(results: any[], menuProps: any) => {
		if (!groupByPath) {
			return null
		}

		if (!labelKey) {
			labelKey = "name"
		}

		// Remove menu props that are not compatible with 'div' DOM element.
		const renderMenuItemChildren = _.get(menuProps, "renderMenuItemChildren")
		const passedProps = _.omit({ ...menuProps }, ["renderMenuItemChildren", "paginationText", "newSelectionPrefix"])

		// Create a flat array instead of rendering a nested loop to avoid render update max depth error
		const grouped = _.groupBy(results, groupByPath)

		const flatArr: any[] = []
		const sortedEntries = _.entries(grouped).sort((a, b) => {
			if (disableSort) {
				return 0
			}
			const ak = a[0]
			const bk = b[0]
			const keyAsc = ak.localeCompare(bk)
			return keyAsc
		})

		for (const [k, v] of sortedEntries) {
			let arr: any[] = []
			const sortedVal = v.sort((a, b) => {
				if (disableSort) {
					return 0
				}
				return _.isFunction(labelKey)
					? labelKey(a)?.localeCompare(labelKey(b))
					: _.get(a, labelKey)?.localeCompare(_.get(b, labelKey))
			})
			if (k === "undefined") {
				arr = [...sortedVal]
			} else {
				arr = [k, ...sortedVal]
			}
			flatArr.push(...arr)
		}

		return (
			<Menu {...passedProps}>
				{flatArr.map((el, i) => {
					if (_.isString(el)) {
						return (
							<div key={i} className="fw-bold ms-2">
								{el}
							</div>
						)
					} else {
						if (renderMenuItemChildren) {
							return (
								<MenuItem key={i} option={el} position={i}>
									{renderMenuItemChildren(el, { ...menuProps, labelKey }, i)}
								</MenuItem>
							)
						}
						return (
							<MenuItem key={i} option={el} position={i}>
								{el.label ?? el.name}
							</MenuItem>
						)
					}
				})}
				{flatArr.length === 0 && !hasNextPage && (
					<MenuItem option={{ disabled: true }} position={0} disabled>
						{t("Ei tuloksia")}
					</MenuItem>
				)}
				{hasNextPage && <div className="text-muted ms-2">{t("Lisätuloksia näkee kirjoittamalla hakutekstin")}</div>}
			</Menu>
		)
	}

// Local constants for test group and subgroup sorting; used instead of enum query for now
const eTrainingGroup = ["antropometry", "physical", "sport_skill", "knowledge", "general", "muscle_balance"]
const eTrainingSubGroup = ["lineaarinopeus", "suunnanmuutosnopeus", "nopeusvoima", "maksimi_ja_kestovoima", "kestävyys"]

/**
 * TypeAhead `renderMenu` function for grouped tests
 */
export const renderMenuTestGroups = (results: any[], menuProps: any, props: any) => {
	const { selected } = props
	const testSelected = selected.filter((o: any) => !o.isGlobalSpec)
	const globalSpecSelected = selected.filter((o: any) => o.isGlobalSpec)

	// Remove menu props that are not compatible with 'div' DOM element.
	const passedProps = _.omit({ ...menuProps }, ["renderMenuItemChildren", "paginationText", "newSelectionPrefix"])

	// Create a flat array instead of rendering a nested loop to avoid render update max depth error
	const getFlatList = (results: any[], selected: any[]) => {
		if (!results || !selected) {
			return []
		}

		if (selected.length > 0) {
			results = results.filter((o) => selected.every((item) => item._id !== o._id))
		}

		const grouped = _.mapValues(_.groupBy(results, "group"), (v) => _.groupBy(v, "subGroup"))

		const flatList: any[] = []

		const sorted = sortByArray(_.keys(grouped), eTrainingGroup)
		sorted.forEach((primaryGroupKey) => {
			if (primaryGroupKey !== "undefined") {
				flatList.push({ type: "primary", label: primaryGroupKey })
			}

			const secondaryGroupObj = grouped[primaryGroupKey]
			const sorted2nd = sortByArray(_.keys(secondaryGroupObj), eTrainingSubGroup)
			sorted2nd.forEach((secondaryGroupKey) => {
				const testArray = secondaryGroupObj?.[secondaryGroupKey]
				if (secondaryGroupKey !== "undefined") {
					flatList.push({ type: "secondary", label: secondaryGroupKey })
				}
				const sortedItems = [...(testArray ?? [])].sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
				const items = sortedItems.map((o) => ({ type: "item", label: o.name, ...o }))
				flatList.push(...items)
			})
		})
		return flatList
	}

	const flatList = getFlatList(
		results.filter((o) => !o.isGlobalSpec),
		testSelected
	)
	const flatListGlobalSpecs = getFlatList(
		results.filter((o) => o.isGlobalSpec),
		globalSpecSelected
	)

	return (
		<Menu {...passedProps}>
			{flatListGlobalSpecs.length > 0 && <h5 style={{ textDecoration: "underline" }}>{t("Testit")}</h5>}
			{flatList.map((el, i) => {
				if (el.type === "primary")
					return (
						<div key={i} className="fw-bold ms-1">
							{i18n.t(`values|trainingGroup~${el.label}`)}
						</div>
					)
				if (el.type === "secondary") {
					return (
						<div key={i} className="fw-bold fst-italic ms-2">
							{i18n.t(`values|trainingGroup~${el.label}`)}
						</div>
					)
				}
				if (el.type === "item") {
					return (
						<MenuItem key={i} option={el} position={i}>
							{el.label}
						</MenuItem>
					)
				}
			})}
			{flatListGlobalSpecs.length > 0 && <h5 style={{ textDecoration: "underline" }}>{t("Johdetut tulokset")}</h5>}
			{flatListGlobalSpecs.map((el, i) => {
				if (el.type === "primary")
					return (
						<div key={i} className="fw-bold ms-1">
							{i18n.t(`values|trainingGroup~${el.label}`)}
						</div>
					)
				if (el.type === "secondary") {
					return (
						<div key={i} className="fw-bold fst-italic ms-2">
							{i18n.t(`values|trainingGroup~${el.label}`)}
						</div>
					)
				}
				if (el.type === "item") {
					return (
						<MenuItem key={i} option={el} position={i}>
							{el.label}
						</MenuItem>
					)
				}
			})}
			{flatList.length === 0 && (
				<MenuItem option={{ disabled: true }} position={0} disabled>
					{t("Ei tuloksia")}
				</MenuItem>
			)}
		</Menu>
	)
}

/**
 * Returns the "highest" role for each event participant
 *
 * @param {[Object]} participants 					Single Event GET participants array
 */
export const getEventParticipantWithHighestRole = (event: any, members: any[] | null = null) => {
	let participants = members ? members : event.participants

	// Filter out participants whose `profile` populated path is null
	participants = participants.filter((p: any) => _.get(p, "profile"))

	const roleOrder = ROLE_ORDER

	const participantsWithRoles = participants.map((participant: any) => {
		const participantRoles = participant.relativeRoles ?? participant.memberRoles

		const roleIndices = participantRoles.map((r: any) => roleOrder.indexOf(r?.name)).filter((i: number) => i !== -1)
		const roleIndex = Math.min(...roleIndices)
		let role = roleOrder[roleIndex]
		if (!role) {
			role = "none"
		}

		return {
			...participant,
			roleIndex,
			role,
		}
	})

	return participantsWithRoles
}

/**
 * Returns applicable surface type options for specific test and specific operational class context.
 *
 * @param {Object} opClass 				Operational class for current user or specific root group
 * @param {Object} test 					Training doc whose surface options are retrieved
 * @returns {Object}							Object with keys "surfaceDefault" and "surfaceChoices"
 */
export const getSurfaceOpts = (opClass: any, test: any) => {
	let surfaceChoices = test.surfaceChoices ?? []
	let surfaceDefault = test.surfaceType ?? ""

	const ctxOpts = _.get(opClass, "trainings")?.find((o: any) => o.doc === test._id)?.options
	if (ctxOpts) {
		if (!_.isEmpty(ctxOpts.surfaceChoices)) {
			surfaceChoices = ctxOpts.surfaceChoices
		}
		if (ctxOpts.surfaceDefault) {
			surfaceDefault = ctxOpts.surfaceDefault
		}
	}

	return { surfaceChoices, surfaceDefault, surfaceType: surfaceDefault }
}

// Hook that uses EventType document to determine parent event status
export const useIsParentEvent = (event: any) => {
	const eventTypeDoc = useEventTypeDoc(event?.eventTypes?.[0])
	return eventTypeDoc?.isParent
}

// Returns function to retrieve parent event status
export const useGetIsParentEvent = () => {
	const fn = useGetEventTypeDoc()
	return (event: any) => !!fn(event?.eventTypes?.[0])?.isParent
}

/**
 * Applies filtering to input `useGetDistinctResultFields` data array to only include
 * items that exist `pageCustomizations.[].tests` array.
 *
 * @param {[Object] | Object} fields 																				Array of data from `api/training/resultFields`
 * @param {Object[]} customTests 																						Array of page customization objects from `opClass.pageCustomizations.[].tests`
 * @param {boolean} useComparable 																					Filter by comparable value of test
 * @returns {import("@myeway/types).TestField[]}														Filtered array of only specific input field objects
 */
export const cFilterDistinctFields = <T extends TestFieldLike>(
	fields: T[] = [],
	customTests: TestFieldLike[] = [],
	useComparable = false
): T[] => {
	if (_.isEmpty(customTests)) {
		return fields
	}

	const getMatch = (o: TestFieldLike) =>
		fields.find((f) => {
			if (!o || !f) {
				return false
			}
			const idMatch = f.testId === o.testId
			if (!idMatch) {
				return false
			}
			if (o.isGlobalSpecial && f.isGlobalSpecial) {
				return true
			}
			if (o.resultIndex != null) {
				return o.resultIndex === f.resultIndex
			}
			if (o.specialIndex != null) {
				return o.specialIndex === f.specialIndex
			}
			return f.resultIndex === 0
		})

	const filtered = customTests
		.map((o) => {
			const match = getMatch(o)
			if (match && (!useComparable || match.comparable)) {
				const copy: TestFieldLike = { ...o, ...match }
				if (copy.children) {
					copy.children = copy.children.map((c) => getMatch(c)).filter((o): o is T => Boolean(o))
				}
				return copy
			}
		})
		.filter((o): o is TestFieldLike => Boolean(o))
	return filtered as T[]
}

/**
 * Retrieves specific `useGetDistinctResultFields` data item based on
 * the input parameters.
 *
 * @param {any[]} fields 						Array of data from `api/training/resultFields`
 * @param {Object} input 								Input object with keys `testId`, `resultIndex`, `specialIndex` and `isGlobalSpecial`,
 * 																			or input string delimited by underscores. Contains interpolated values in the order listed above.
 * @returns {Object}										Found field object
 */
export const cGetField = (fields: TestFieldLike[] = [], input?: any) => {
	let testId: any, resultIndex: any, specialIndex: any, isGlobalSpecial: any
	if (_.isObject(input)) {
		const objectInput = input as TestFieldLike
		testId = objectInput.testId
		resultIndex = objectInput.resultIndex
		specialIndex = objectInput.specialIndex
		isGlobalSpecial = objectInput.isGlobalSpecial
	} else if (_.isString(input)) {
		const parts = input.split("_")
		testId = parts[0]

		resultIndex = parts[1]
		if (resultIndex === "" || resultIndex == null || resultIndex === "undefined") {
			resultIndex = null
		} else {
			resultIndex = parseInt(resultIndex)
		}

		specialIndex = parts[2]
		if (specialIndex === "" || specialIndex == null || specialIndex === "undefined") {
			specialIndex = null
		} else {
			specialIndex = parseInt(specialIndex)
		}

		isGlobalSpecial = !_.isEmpty(parts[3]) && parts[3] !== "undefined"
	} else {
		return fields[0]
	}

	const found = fields.find((f) => {
		const idMatch = f.testId === testId
		if (!idMatch) {
			return false
		}
		if (isGlobalSpecial && f.isGlobalSpecial) {
			return true
		}
		if (resultIndex != null) {
			return resultIndex === f.resultIndex
		}
		if (specialIndex != null) {
			return specialIndex === f.specialIndex
		}
		return resultIndex === 0
	})
	return found
}

const shouldAnswerPersonalDiary = (member: any, diaryTask: any) => {
	if (!member) {
		return false
	}
	const memberRoles = member?.relativeRoles?.map((o: any) => o?.name) ?? []
	const rolesWPersonalDiary = _.get(diaryTask, ["taskDefinition", "templateRoleConfig", "hasPersonalDiary"])
	return memberRoles.some((r: any) => rolesWPersonalDiary.includes(r))
}

const shouldAnswerPeerDiary = (member: any, diaryTask: any) => {
	if (!member) {
		return false
	}
	const memberRoles = member?.relativeRoles?.map((o: any) => o?.name) ?? []
	const rolesWPeerDiary = _.get(diaryTask, ["taskDefinition", "templateRoleConfig", "hasPeerDiaries"])
	return memberRoles.some((r: any) => rolesWPeerDiary.includes(r))
}

export const getDiaryTaskStatusForMember = (member: any, diaryTasks: any[]) => {
	let status = null
	for (const diaryTask of diaryTasks) {
		const context = diaryTask.context ?? _.get(diaryTask, "entryCount.relevantContext")
		if (context === "peer") {
			if (shouldAnswerPeerDiary(member, diaryTask.diaryTask)) {
				const some = diaryTask.diaryAnswerers.some((pId: any) => member.profile._id === pId)
				if (!some) return false
				else status = true
			}
		} else {
			if (shouldAnswerPersonalDiary(member, diaryTask.diaryTask)) {
				const some = diaryTask.diaryAnswerers.some((pId: any) => member.profile._id === pId)
				if (!some) return false
				else status = true
			}
		}
	}
	return status
}

export const boolIcon = (bool: boolean | null | undefined) => {
	if (bool === true) return <Check color="green" size={24} />
	else if (bool === false) return <X color="red" size={24} />
	return <Dash size={24} />
}

/**
 * Three-state survey answer status of a member: `true` when an answer has been given, `false` when an answer is
 * expected but missing, and `null` when no answer is expected from the member in the task.
 */
const getMemberSurveyAnswerStatus = (member: any, task: any, answererIds: any[], profileDoneMap: Record<string, boolean>) => {
	const profileId = member?.profile?._id?.toString()
	const state = task?.perProfileState?.find((o: any) => (o.profile?._id ?? o.profile)?.toString() === profileId)

	const hasAnswer =
		answererIds.some((id: any) => id?.toString() === profileId) ||
		!!_.get(state, ["entryData", "personal", "hasEntry"]) ||
		!!_.get(state, ["entryData", "peerInitiator", "hasEntry"]) ||
		profileDoneMap[profileId] === true
	if (hasAnswer) {
		return true
	}

	// Members missing from `perProfileState` are not expected to answer, eg. because the task is assigned to
	// someone else. `profileDoneMap` is used as a fallback for views that do not include `perProfileState`.
	const isExpected = state
		? !!_.get(state, ["entryData", "personal", "expected"]) || !!_.get(state, ["entryData", "peerInitiator", "expected"])
		: _.has(profileDoneMap, profileId)

	return isExpected ? false : null
}

export const getEventParticipantsWithSurveyStatuses = (event: any, task: any) => {
	const diaryAnswerers = task?.entryCount?.respondentIdsAny ?? task?.entryCount?.respondentIds ?? []
	const profileDoneMap = task.profileDoneMap ?? {}
	// `perProfileState` accounts for task assignment and profiles the task has been opened for, which member roles do not
	const hasPerProfileState = (task?.perProfileState?.length ?? 0) > 0
	const membersDataWithRoles = getEventParticipantWithHighestRole(event).filter((m: any) =>
		hasPerProfileState
			? getMemberSurveyAnswerStatus(m, task, diaryAnswerers, profileDoneMap) !== null
			: shouldAnswerPeerDiary(m, task) || shouldAnswerPersonalDiary(m, task)
	)
	const sortedMembers = [...membersDataWithRoles].map((m) => ({
		...m,
		surveyAnswer: getMemberSurveyAnswerStatus(m, task, diaryAnswerers, profileDoneMap),
	}))
	sortedMembers.sort((a, b) => a?.profile?.firstname?.localeCompare(b?.profile?.firstname))
	sortedMembers.sort((a, b) => a?.profile?.lastname?.localeCompare(b?.profile?.lastname))
	return sortedMembers
}
export const convertHMS = (value: string | number) => {
	const sec = parseInt(String(value), 10) // convert value to number if it's string
	let hours: string | number = Math.floor(sec / 3600) // get hours
	let minutes: string | number = Math.floor((sec - Number(hours) * 3600) / 60) // get minutes
	let seconds: string | number = sec - Number(hours) * 3600 - Number(minutes) * 60 //  get seconds
	// add 0 if value < 10; Example: 2 => 02
	if (hours < 10) {
		hours = "0" + hours
	}
	if (minutes < 10) {
		minutes = "0" + minutes
	}
	if (seconds < 10) {
		seconds = "0" + seconds
	}
	return hours + ":" + minutes + ":" + seconds // Return is HH : MM : SS
}

/**
 * Return boolean on whether specific root group belongs to a customer group
 * based on their assigned customer group or class. Less specific customer groups
 * have more matches.
 *
 * @param {Object} rootGroup 					Root Group which is checked. Must either have populated `customerGroups` path or `classes` path
 * @param {Object} customerGroup 			Customer group to which the root group is compared against. Must have id arrays of rgc documents.
 * @returns {Boolean}									Whether root group belongs to a customer group.
 */
export const isWithinCustomerGroup = (rootGroup: any, customerGroup: any) => {
	// Ensure that we have base amount of data required to compute boolean answer
	if (
		(!rootGroup?.customerGroups && !rootGroup?.classes) ||
		!customerGroup?.operationalIds ||
		_.isNil(customerGroup?.operationalIds?.length)
	) {
		return false
	}

	let opIds = _.flatMap(rootGroup.customerGroups, "operationalIds")
	if (_.isEmpty(opIds)) {
		opIds = rootGroup.classes.filter((c: any) => c.type === "operational").map((o: any) => o._id)
	}

	let proIds = _.flatMap(rootGroup.customerGroups, "processIds")
	if (_.isEmpty(proIds)) {
		proIds = rootGroup.classes.filter((c: any) => c.type === "process").map((o: any) => o._id)
	}

	let prdLvlIds = _.flatMap(rootGroup.customerGroups, "productLevelIds")
	if (_.isEmpty(prdLvlIds)) {
		prdLvlIds = rootGroup.classes.filter((c: any) => c.type === "productLvl").map((o: any) => o._id)
	}

	const cg = customerGroup
	if (cg.operationalIds.length > 0 && cg.processIds?.length > 0 && cg.productLevelIds?.length > 0) {
		return (
			opIds.some((id) => cg.operationalIds.includes(id)) &&
			proIds.some((id) => cg.processIds.includes(id)) &&
			prdLvlIds.some((id) => cg.productLevelIds.includes(id))
		)
	}
	if (cg.operationalIds.length > 0 && cg.processIds?.length > 0) {
		return opIds.some((id) => cg.operationalIds.includes(id)) && proIds.some((id) => cg.processIds.includes(id))
	}
	if (cg.operationalIds.length > 0) {
		// Also compare by object since in some cases `cg.operationalIds` may be an object with field `_id`.
		return opIds.some((id) => cg.operationalIds.includes(id) || cg.operationalIds.some((obj: any) => obj._id === id))
	}
	return true
}

export const getTestFieldIdStr = (testField: TestFieldLike | null | undefined) => {
	if (!testField) {
		return null
	}
	return `${testField.testId}_${testField.resultIndex}_${testField.specialIndex}_${testField.isGlobalSpecial}`
}

// Reused function for finding 'resultIndex-specialIndex-isGlobalSpecial' identifiable items from an array
export const getTestFieldMatch = (arr: TestFieldLike[] = [], testField: TestFieldLike | null | undefined, useFilter = false): any => {
	if (!testField) {
		return useFilter ? [] : null
	}
	if (!arr) {
		arr = []
	}
	const fn = (r: TestFieldLike) => {
		if (testField.resultIndex != null) {
			return testField.resultIndex === r.resultIndex
		}
		if (testField.specialIndex != null) {
			return testField.specialIndex === r.specialIndex
		}
		if (testField.isGlobalSpecial && r.isGlobalSpecial) {
			return true
		}
		return r.resultIndex === 0
	}
	return useFilter ? arr.filter(fn) : arr.find(fn)
}

// Format bytes
export const formatBytes = (bytes: number, decimals = 2) => {
	if (!+bytes) return "0 Bytes"

	const k = 1000
	const dm = decimals < 0 ? 0 : decimals
	const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

	const i = Math.floor(Math.log(bytes) / Math.log(k))

	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export const embedTestFieldData = (fields: TestFieldLike[], data: any): any[] => {
	// Embed associated data into test field
	const formatField = (f: TestFieldLike) => {
		const newData: AnyRecord = {}
		_.keys(data).forEach((profileId) => {
			const meta = _.get(data, [profileId, "meta"])
			const testObj = _.get(data, [profileId, "tests", f.testId])
			const latestArr = _.get(testObj, "latestEntry")
			const testEntryLatest = getTestFieldMatch(latestArr, f)
			const previousArr = _.get(testObj, "previousEntry")
			const testEntryPrevious = getTestFieldMatch(previousArr, f)
			_.set(newData, profileId, { meta, latest: testEntryLatest, previous: testEntryPrevious })
		})
		return { ...f, profileData: newData }
	}

	// Embed associated data into test fields
	const getProcessedFields = (fields: TestFieldLike[]) => {
		const result = fields.map((f) => {
			const r = formatField(f)
			if (r.children) {
				r.children = r.children.map((c) => formatField(c))
			}
			return r
		})
		return result
	}

	return getProcessedFields(fields)
}
/**
 * Filters profiles for `<ProfileTypeahead />` in admin-interface using formik's values -parameters.
 * @param {{ linkedCustomerGroups, rootGroupsWithAccess, groupsWithAccess }} values - Formik values object that must have these fields as lists: **rootGroupsWithAccess**, **groupsWithAccess**.
 */
export const getSearchProfileEndpointQueryByVideoVisibilityFilters = (values: any) => {
	const { linkedCustomerGroups, rootGroupsWithAccess, groupsWithAccess, rolesWithAccess } = values

	const queryExtra = {
		...(!_.isEmpty(linkedCustomerGroups) && { customerGroupIds: linkedCustomerGroups.map((cg: any) => cg._id) }),
		...(!_.isEmpty(rootGroupsWithAccess) && { rootGroupIds: rootGroupsWithAccess.map((rg: any) => rg._id) }),
		...(!_.isEmpty(groupsWithAccess) && { groupIds: groupsWithAccess.map((g: any) => g._id) }),
		...(!_.isEmpty(rolesWithAccess) && { role: rolesWithAccess.map((r: any) => r.name) }),
	}

	return queryExtra
}
export const getTestDuplicatesInTasks = (tasks: any[], trainingsData: any[]) => {
	if (!tasks) {
		return []
	}
	const singleTestTasks = tasks.filter((tsk) => tsk.type === "test_results")
	const protocolTestTasks = tasks.filter((tsk) => tsk.type === "test_results_protocol")
	const allTests = _.compact(singleTestTasks.map((tsk) => tsk.doc).concat(_.flatMap(protocolTestTasks, "protocolTestSubset")))
	const duplicateTestIds = _.filter(allTests, (val, i, iteratee) => _.includes(iteratee, val, i + 1))
	if (!trainingsData) {
		return duplicateTestIds
	}
	const duplicateTestNames = duplicateTestIds.map((id) => trainingsData?.find((o) => o._id === id)).map((o) => o.name)
	return duplicateTestNames
}

// Utility used by the downloader functions. Returns extension including the dot.
const determineDestinationFileName = (url: string, fallbackExtension: string) => {
	const getExtension = (str: string | undefined) => str?.slice(str.lastIndexOf(".")) || fallbackExtension
	const parts = url.split("/")
	const fileNameAndMaybeExtensionInUrl = _.last(parts)
	try {
		const extension = getExtension(fileNameAndMaybeExtensionInUrl)
		return extension
	} catch {
		return fallbackExtension
	}
}

function isFirefox() {
	return navigator.userAgent.includes("Firefox") && !navigator.userAgent.includes("Seamonkey")
}

/**
 * Util function to handle downloading of files into browser across web browsers.
 *
 * Note about the required file type; browser *can* automatically determine the type of the downloaded file, but this is unreliable.
 * Edge can detect a .mp4 file as .xml for instance. Hence why it is required to pass the file type as well.
 *
 * Disclaimer: DOES NOT WORK RELIABLY ON LOCALHOST FOR SOME DOWNLOADS!
 *
 * Tested to work on these browsers at the time of writing (2025/1/3):
 * 	- Chrome 131.0.6778
 *  - Firefox 133.0.3
 *  - Edge 131.0.2903
 */
export const download = async (args: any) => {
	const { url, onDownloadStarted, onDownloadError, onDownloadProgress } = args
	let response
	try {
		onDownloadStarted?.()
		if (isFirefox()) {
			response = await fetch(url, { redirect: "follow" })
			if (response.status >= 400 && response.status <= 599) {
				onDownloadError(response)
				return
			}
			const contentLength = response.headers.get("Content-Length")
			let blob
			if (contentLength) {
				const total = parseInt(contentLength, 10)
				let loaded = 0
				const reader = response.body?.getReader()
				if (!reader) {
					throw new Error("Response body is not readable")
				}
				const chunks: BlobPart[] = []
				let done = false
				while (!done) {
					const res = await reader.read()
					done = res.done
					if (done) break
					if (!res.value) continue
					chunks.push(res.value)
					loaded += res.value.length
					onDownloadProgress?.(loaded / total)
				}
				blob = new Blob(chunks)
			} else {
				onDownloadProgress?.(1)
				blob = await response.blob() // fallback
			}
			const downloadUrl = URL.createObjectURL(blob)
			await downloadWithLinkTag({ ...args, url: downloadUrl })
			window.URL.revokeObjectURL(downloadUrl)
		} else {
			await downloadWithLinkTag(args)
			onDownloadProgress?.(1)
		}
	} catch (err) {
		onDownloadError(err)
	}
}

const downloadWithLinkTag = async ({ url, fileName, fileType }: any) => {
	const link = document.createElement("a")
	link.href = url
	link.download = fileName + (fileType ? `.${fileType}` : determineDestinationFileName(url, "mp4"))
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}

export const getGroupAgeGroupDemo = (group: any) => {
	const { sex, birthYear } = group

	const age = differenceInCalendarYears(new Date(), new Date(`${birthYear}-01-01`))

	let demoLabelAgeGroup
	if (age >= 20) {
		demoLabelAgeGroup = sex === "male" ? "Miehet" : "Naiset"
	} else {
		const letter = sex === "male" ? "P" : "T"
		let number: string | number = _.floor(age).toString()
		if (Number(number) < 10) {
			number = 10
		}
		demoLabelAgeGroup = letter + number
	}
	return demoLabelAgeGroup
}

/**
 * General-purpose function that returns a Date format string for displaying
 * an event documents start and end times. This is dependent on whether the
 * event spans the whole day and if it spans multiple days
 *
 * @param {Object} event 											Event object
 * @param {Object} opts												Options
 * @param {Boolean} opts.omitSingleDayDate 		Hide the date part in a single-day event. Used when the date is known
 *                                            through other context, eg. calendar with a day cell selected.
 * @param {Boolean} opts.displayYear					Include year in event date string
 * @returns {Object}													Object containing date format strings for startTime and endTime
 */
const getEventDateFormat = (event: any, { omitSingleDayDate, displayYear }: EventDateOptions = {}) => {
	const baseDate = displayYear ? "dd.MM.yyyy" : "dd.MM"
	const baseTime = "HH:mm"
	const baseDateTime = baseDate + " " + baseTime

	if (!event) {
		return { startTime: baseDateTime, endTime: baseTime }
	}

	const startTime = new Date(event.startTime)
	const endTime = new Date(event.endTime)

	const isAllDay = event.allday
	const isMultiDay = differenceInCalendarDays(endTime, startTime) !== 0

	/**
	 * 4 cases:
	 * 1. Event is a single day event that is not all day:
	 * 16.08 12:00 - 14:00
	 * 2. Event is a single day event that is all day
	 * 16.08
	 * 3. Event is a multi-day event that is not all day
	 * 16.08 12:00 - 20.08 14:00
	 * 4. Event is a multi-day event that is all day
	 * 16.08 - 20.08
	 */
	let startFormat = ""
	let endFormat = ""
	if (isAllDay) {
		if (isMultiDay) {
			startFormat = baseDate
			endFormat = baseDate
		} else {
			startFormat = baseDate
			endFormat = ""
		}
	} else {
		if (isMultiDay) {
			startFormat = baseDateTime
			endFormat = baseDateTime
		} else {
			startFormat = omitSingleDayDate ? baseTime : baseDateTime
			endFormat = baseTime
		}
	}

	return { startTime: startFormat, endTime: endFormat }
}

/**
 * Companion function for `getEventDateFormat`. Returns the complete Strign
 * presentation of the event's time dependent on whether the event spans the
 * whole day and whether it spans multiple days.
 *
 * @param {Object=?} event 											Event object
 * @param {Object} opts												Options
 * @param {Boolean=} opts.omitSingleDayDate 		Hide the date part in a single-day event. Used when the date is known
 *                                            through other context, eg. calendar with a day cell selected.
 * @param {Boolean=} opts.displayYear					Include year in event date string
 * @param {Boolean=} opts.omitEndTime					Omit event end time
 * @param {Boolean=} opts.displayDuration			Append event duration to the end of the returned string
 * @returns {String}													String representation of event date
 */
export const getEventDateString = (
	event: any,
	{ omitSingleDayDate, displayYear, omitEndTime, displayDuration }: EventDateOptions = {}
) => {
	if (!event) {
		return ""
	}
	const { startTime: startFormat, endTime: endFormat } = getEventDateFormat(event, { omitSingleDayDate, displayYear })
	const startStr = formatInTimeZone(new Date(event.startTime), TZ, startFormat)
	const endStr = !endFormat || !event.endTime ? "" : formatInTimeZone(new Date(event.endTime), TZ, endFormat)
	let resultStr = omitEndTime ? startStr : `${startStr}${endStr ? " - " : ""}${endStr}`
	if (displayDuration && event.startTime && event.endTime) {
		const duration = getDurationString(new Date(event.startTime), new Date(event.endTime))
		resultStr += ` (${duration})`
	}

	return resultStr
}

/**
 * Reused filter function on different training result views and excel import functions
 *
 * @param {[Object]} participantsData					Array of Participant documents
 * @returns {[Object]}												Filtered participans
 */
export const testResultParticipantFilter = (participantsData: any[]) => {
	return participantsData.filter((p) => p.subordinate && p.attended)
}

export const getActiveProfileIds = (selectedGroups: any[] = [], selectedPlayer?: any) => {
	const groupProfileIds = selectedGroups.flatMap((g) => g.members.map((m: any) => m.profile?._id))
	const playerProfileIds = selectedPlayer?.id ? [selectedPlayer.id] : []
	return _.uniq([...groupProfileIds, ...playerProfileIds])
}

/**
 * Helper for React.memo implementation that tracks a specific list of props.
 * Equality is checked with lodash _.isEqual, which allows deep comparison of arrays
 * and objects.
 *
 * Does logging when VITE_LOG_APPLY_MEMO is enabled.
 *
 * @param {Component} component 				React component
 * @param {string[]} trackedProps				List of prop names that should be tracked
 * @returns {*}
 */
export const applyMemo = <P extends object>(component: ComponentType<P>, trackedProps: string[]) => {
	return memo(component, (prevProps, nextProps) => {
		const propsEqual = trackedProps.every((path) => {
			const b = _.isEqual(_.get(prevProps, path), _.get(nextProps, path))
			if (LOG_APPLY_MEMO && !b) {
				console.log(`${component.name}: ${path} PROP NOT EQUAL`)
			}
			return b
		})
		return propsEqual
	})
}

export const getAlphaSort = (k: string, asc: boolean) =>
	asc ? (a: any, b: any) => _.get(a, k)?.localeCompare(_.get(b, k)) : (b: any, a: any) => _.get(a, k)?.localeCompare(_.get(b, k))
export const getNumSort = (k: string, asc: boolean) =>
	asc ? (a: any, b: any) => _.get(a, k) - _.get(b, k) : (b: any, a: any) => _.get(a, k) - _.get(b, k)

// Matching used in default 'filterBy' algorithm from 'react-bootstrap-typeahead'
export const typeaheadIsMatch = (input: string, string: string, props: any) => {
	let searchStr = input
	let str = string
	if (!props.caseSensitive) {
		searchStr = searchStr.toLowerCase()
		str = str.toLowerCase()
	}
	if (props.ignoreDiacritics) {
		//  Not used
	}
	return str.indexOf(searchStr) !== -1
}

/** Returns whether user should only be able to see videos linked to events. */
export const hasEerikkilaLibraryPerm = ({ profile, rootGroupId, groupRole }: any) => {
	const roles = _.get(profile, ["groupRoleOverview", rootGroupId, "memberRoles"])
	const hasEerikkilaLibraryAccess = !!roles
		?.find((mr: any) => mr.name === groupRole)
		?.itemPerms?.find((p: any) => p.resource === "video" && p.flags?.includes("eerikkilaLibraryAccess"))
	return hasEerikkilaLibraryAccess
}

/** Returns new object that has the key-value pair changes between two objects. */
export const changesBetweenObjects = (objectA: AnyRecord, objectB: AnyRecord) => {
	const changes = _.fromPairs(_.differenceWith(_.toPairs(objectA), _.toPairs(objectB), _.isEqual))
	return changes
}
export const promptConfirmationDialog = ({
	dispatch,
	title,
	body,
	variant,
	okLabel,
	callback,
}: {
	dispatch: Dispatch
	title: string
	body: string
	variant?: string
	okLabel?: string
	callback: () => void
}) => {
	dispatch(
		setConfirmModal({
			title,
			body,
			variant,
			okLabel,
			okFn: () => callback(),
		})
	)
}
